import API_CONFIG from '../config/api';
import { logger } from '../logger';

const withBase = (path) =>
  API_CONFIG.BASE_URL
    ? `${API_CONFIG.BASE_URL.replace(/\/$/, '')}${path}`
    : path;

const withQuery = (url, params) => {
  const u = new URL(withBase(url), API_CONFIG.BASE_URL || window.location.origin);
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) u.searchParams.set(k, String(v));
  });
  return u.toString();
};

async function doFetch(input, init = {}, timeoutMs = API_CONFIG.TIMEOUT) {
  const method = (init.method || 'GET').toUpperCase();
  const finalUrl = typeof input === 'string' ? input : input?.toString();

  // Dev logging of the final URL/method for troubleshooting
  if (process.env.NODE_ENV !== 'production') {
    try {
      logger.debug('[ApiClient raw request]', method, finalUrl);
    } catch {}
  }

  const controller = new AbortController();
  let timerId = null;
  if (timeoutMs > 0) {
    // Abort with a reason so we can detect timeout specifically
    timerId = setTimeout(() => {
      try {
        controller.abort(new DOMException(`Timeout after ${timeoutMs}ms (${method} ${finalUrl})`, 'TimeoutError'));
      } catch {
        // Fallback for browsers not supporting reason
        controller.abort();
      }
    }, timeoutMs);
  }

  try {
    const res = await fetch(input, { ...init, signal: controller.signal });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const err = new Error(`HTTP ${res.status} (${method} ${finalUrl}): ${text || res.statusText}`);
      err.status = res.status;
      err.body = text;
      err.url = finalUrl;
      err.method = method;
      throw err;
    }
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) return await res.json();

    // Detect accidental frontend HTML (index.html) instead of API JSON
    const text = await res.text();
    if (/<!doctype html>/i.test(text)) {
      const err = new Error(`Non-API HTML received at ${finalUrl}. Backend URL misconfigured.`);
      err.status = 0;
      throw err;
    }
    return text;
  } catch (e) {
    // Normalize Abort/Timeout errors
    const isAbort = e?.name === 'AbortError' || e?.name === 'TimeoutError';
    if (isAbort) {
      const timeoutError = new Error(
        e?.message?.includes('Timeout') ? e.message : `Timeout after ${timeoutMs}ms (${method} ${finalUrl})`
      );
      timeoutError.code = 'ETIMEDOUT';
      timeoutError.url = finalUrl;
      timeoutError.method = method;
      throw timeoutError;
    }
    throw e;
  } finally {
    if (timerId) clearTimeout(timerId);
  }
}

export const ApiClient = {
  retrieve: async ({ project, question, userid, user, conversation_id, question_no }) => {
    const path = API_CONFIG.ENDPOINTS.RETRIEVE;
    const url = withBase(path);
    // accept either user or userid from caller
    const effectiveUser = userid || user;
    const body = {
      project,
      question,
      userid: effectiveUser,
      ...(conversation_id ? { conversation_id } : {}),
      ...(Number.isFinite(question_no) ? { question_no } : {})
    };

    logger.debug('[ApiClient.retrieve] POST', url, body);

    try {
      const result = await doFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      logger.debug('[ApiClient.retrieve] response (POST):', result);
      return result;
    } catch (err) {
      if (err && (err.status === 404 || err.status === 405)) {
        const getUrl = withQuery(path, body);
        logger.warn('[ApiClient.retrieve] POST failed with', err.status, '- retrying GET', getUrl);
        const result = await doFetch(getUrl, { method: 'GET' });
        logger.debug('[ApiClient.retrieve] response (GET):', result);
        return result;
      }
      logger.error('[ApiClient.retrieve] request failed:', err);
      throw err;
    }
  },

  getConversations: async ({ project, user }) => {
    const path = API_CONFIG.ENDPOINTS.GET_CONVERSATION;
    const payload = { project, user };
    logger.debug('[ApiClient.getConversations] POST', path, payload);
    try {
      const res = await doFetch(withBase(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      logger.debug('[ApiClient.getConversations] raw response:', res);

      let raw = Array.isArray(res?.logs) ? res.logs : [];
      if (!raw.length) {
        if (Array.isArray(res)) raw = res;
        else if (Array.isArray(res?.conversations)) raw = res.conversations;
        else if (Array.isArray(res?.data)) raw = res.data;
        else if (Array.isArray(res?.items)) raw = res.items;
        else if (res && (res.id || res.Title)) raw = [res];
      }

      // Keep every entry (no dedup)
      const sanitize = (s) =>
        (s || '')
          .replace(/^[\s*_`"'~-]+/, '')    // removed unnecessary escape
          .replace(/[\s*_`"'~-]+$/, '')    // removed unnecessary escape
          .trim();

      const conversations = raw.map(o => {
        const convId = o.conversation_id || o._id || o.id;
        return {
          id: o.id || o._id || (convId && o.question_no != null ? `${convId}-${o.question_no}` : convId || 'unknown'),
          fetchId: convId || 'unknown',
          title: sanitize(o.Title || o.title || o.question || o.actual_question || convId || 'Conversation'),
          timestamp: o.timestamp || o.created_at || o.updated_at || null
        };
      }).filter(c => c.fetchId !== 'unknown');

      if (!conversations.length) {
        logger.warn('[ApiClient.getConversations] No conversations parsed from response keys:', Object.keys(res || {}));
      }

      return { conversations };
    } catch (err) {
      logger.error('[ApiClient.getConversations] FAILED:', {
        message: err.message,
        status: err.status,
        url: err.url,
        body: err.body
      });
      throw err;
    }
  },

  getConversationById: async ({ project, user, conversation_id }) => {
    const path = API_CONFIG.ENDPOINTS.GET_CONVERSATION;
    const payload = {
      project,
      user,
      conversation_id
    };
    logger.debug('[ApiClient.getConversationById] POST', path, payload);
    try {
      const res = await doFetch(withBase(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const logs = Array.isArray(res?.logs)
        ? res.logs
        : Array.isArray(res)
          ? res
          : (res && (res.id || res.Title) ? [res] : []);
      if (!logs.length) {
        logger.warn('[ApiClient.getConversationById] empty logs for conversation_id:', conversation_id);
      }
      return { logs };
    } catch (err) {
      logger.error('[ApiClient.getConversationById] FAILED:', {
        message: err.message,
        status: err.status,
        url: err.url,
        body: err.body
      });
      throw err;
    }
  },

  health: async () => {
    return doFetch(withBase(API_CONFIG.ENDPOINTS.HEALTH), { method: 'GET' });
  }
};
